import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  getFirestore,
} from 'firebase/firestore';
import { firebaseApp } from './firebaseConfig';

const db = getFirestore(firebaseApp);

export type ReviewStatus = 'pending' | 'reviewed' | 'approved' | 'rejected';

export interface ReviewRequest {
  id?: string;
  studentId: string;
  studentName: string;
  studentEmail?: string;
  studentPhotoUrl?: string;
  rabbiId: string;
  parashaKey: string;
  parashaHebrew?: string;
  verseIndex: number;
  verseText: string; // Hebrew text of the pasuk
  audioUrl: string; // URL to student's recording (S3 or blob converted)
  status: ReviewStatus;
  createdAt: Timestamp;
  rabbiResponse?: string;
  respondedAt?: Timestamp;
  score?: number;
}

/**
 * Create a new review request from student to rabbi
 */
export async function createReviewRequest(
  request: Omit<ReviewRequest, 'id' | 'createdAt' | 'status'>,
): Promise<string | undefined> {
  try {
    const reviewRequestsRef = collection(db, 'reviewRequests');
    const docRef = await addDoc(reviewRequestsRef, {
      ...request,
      status: 'pending' as ReviewStatus,
      createdAt: Timestamp.now(),
    });
    console.log('Review request created with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error creating review request:', error);
    return undefined;
  }
}

/**
 * Get all review requests for a specific rabbi
 */
export async function getReviewRequestsForRabbi(rabbiId: string): Promise<ReviewRequest[]> {
  try {
    const reviewRequestsRef = collection(db, 'reviewRequests');
    const q = query(
      reviewRequestsRef,
      where('rabbiId', '==', rabbiId),
      orderBy('createdAt', 'desc'),
    );
    const querySnapshot = await getDocs(q);

    const requests: ReviewRequest[] = [];
    querySnapshot.forEach((docSnap) => {
      requests.push({
        id: docSnap.id,
        ...(docSnap.data() as Omit<ReviewRequest, 'id'>),
      });
    });

    return requests;
  } catch (error) {
    console.error('Error fetching review requests for rabbi:', error);
    return [];
  }
}

/**
 * Get all review requests for a specific student
 */
export async function getReviewRequestsForStudent(studentId: string): Promise<ReviewRequest[]> {
  try {
    const reviewRequestsRef = collection(db, 'reviewRequests');
    const q = query(
      reviewRequestsRef,
      where('studentId', '==', studentId),
      orderBy('createdAt', 'desc'),
    );
    const querySnapshot = await getDocs(q);

    const requests: ReviewRequest[] = [];
    querySnapshot.forEach((docSnap) => {
      requests.push({
        id: docSnap.id,
        ...(docSnap.data() as Omit<ReviewRequest, 'id'>),
      });
    });

    return requests;
  } catch (error) {
    console.error('Error fetching review requests for student:', error);
    return [];
  }
}

/**
 * Subscribe to review requests for a rabbi (real-time updates)
 */
export function subscribeToRabbiReviewRequests(
  rabbiId: string,
  onUpdate: (requests: ReviewRequest[]) => void,
): Unsubscribe {
  const reviewRequestsRef = collection(db, 'reviewRequests');
  const q = query(reviewRequestsRef, where('rabbiId', '==', rabbiId), orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const requests: ReviewRequest[] = [];
    snapshot.forEach((docSnap) => {
      requests.push({
        id: docSnap.id,
        ...(docSnap.data() as Omit<ReviewRequest, 'id'>),
      });
    });
    onUpdate(requests);
  });
}

/**
 * Subscribe to review requests for a student (real-time updates)
 */
export function subscribeToStudentReviewRequests(
  studentId: string,
  onUpdate: (requests: ReviewRequest[]) => void,
): Unsubscribe {
  const reviewRequestsRef = collection(db, 'reviewRequests');
  const q = query(
    reviewRequestsRef,
    where('studentId', '==', studentId),
    orderBy('createdAt', 'desc'),
  );

  return onSnapshot(q, (snapshot) => {
    const requests: ReviewRequest[] = [];
    snapshot.forEach((docSnap) => {
      requests.push({
        id: docSnap.id,
        ...(docSnap.data() as Omit<ReviewRequest, 'id'>),
      });
    });
    onUpdate(requests);
  });
}

/**
 * Rabbi responds to a review request
 */
export async function respondToReviewRequest(
  requestId: string,
  response: {
    status: ReviewStatus;
    rabbiResponse?: string;
    score?: number;
  },
): Promise<boolean> {
  try {
    const requestRef = doc(db, 'reviewRequests', requestId);

    // Build update object, excluding undefined values (Firestore doesn't accept undefined)
    const updateData: Record<string, any> = {
      status: response.status,
      respondedAt: Timestamp.now(),
    };

    if (response.rabbiResponse !== undefined) {
      updateData.rabbiResponse = response.rabbiResponse;
    }

    if (response.score !== undefined) {
      updateData.score = response.score;
    }

    await updateDoc(requestRef, updateData);
    console.log('Review request updated:', requestId);
    return true;
  } catch (error) {
    console.error('Error responding to review request:', error);
    return false;
  }
}

/**
 * Get a single review request by ID
 */
export async function getReviewRequestById(requestId: string): Promise<ReviewRequest | null> {
  try {
    const requestRef = doc(db, 'reviewRequests', requestId);
    const docSnap = await getDoc(requestRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...(docSnap.data() as Omit<ReviewRequest, 'id'>),
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching review request:', error);
    return null;
  }
}

/**
 * Count pending review requests for a rabbi
 */
export async function countPendingRequestsForRabbi(rabbiId: string): Promise<number> {
  try {
    const reviewRequestsRef = collection(db, 'reviewRequests');
    const q = query(
      reviewRequestsRef,
      where('rabbiId', '==', rabbiId),
      where('status', '==', 'pending'),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error counting pending requests:', error);
    return 0;
  }
}

/**
 * Check if a review request already exists for this verse
 */
export async function checkExistingRequest(
  studentId: string,
  rabbiId: string,
  parashaKey: string,
  verseIndex: number,
): Promise<ReviewRequest | null> {
  try {
    const reviewRequestsRef = collection(db, 'reviewRequests');
    const q = query(
      reviewRequestsRef,
      where('studentId', '==', studentId),
      where('rabbiId', '==', rabbiId),
      where('parashaKey', '==', parashaKey),
      where('verseIndex', '==', verseIndex),
      where('status', '==', 'pending'),
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const docSnap = querySnapshot.docs[0];
      return {
        id: docSnap.id,
        ...(docSnap.data() as Omit<ReviewRequest, 'id'>),
      };
    }
    return null;
  } catch (error) {
    console.error('Error checking existing request:', error);
    return null;
  }
}

/**
 * Delete a review request by ID
 */
export async function deleteReviewRequest(requestId: string): Promise<boolean> {
  try {
    const requestRef = doc(db, 'reviewRequests', requestId);
    await deleteDoc(requestRef);
    console.log('Review request deleted:', requestId);
    return true;
  } catch (error) {
    console.error('Error deleting review request:', error);
    return false;
  }
}
